import {
  CREATE_REPORT_TEMPLATE_URL,
  GENERATE_MANAGED_REPORT_URL,
  UPDATE_REPORT_TEMPLATE_URL,
} from "@/utils/constants/constants";
import axios from "axios";
import {
  build_one_off_template_name,
  build_question_answer_filters,
  build_question_metric_rows,
  build_question_scope_filters,
  build_report_columns,
  extract_report_builder_error_message,
} from "./report_generation_builder_helpers";

const show_validation_error = (component, message) => {
  if (!message) {
    return false;
  }
  component.show_error(message);
  return true;
};

const get_missing_date_dimension_message = (component) => {
  if ((component.selected_date_from || component.selected_date_to) && !component.selected_date_dimension_id) {
    return "Please select a date dimension.";
  }
  return "";
};

const get_missing_date_boundary_message = (component) => {
  if (!component.selected_date_dimension_id) {
    return "";
  }
  const has_no_boundaries = !component.selected_date_from && !component.selected_date_to;
  if (has_no_boundaries) {
    return "Please select at least one date boundary.";
  }
  return "";
};

const get_invalid_date_order_message = (component) => {
  if (!component.selected_date_from || !component.selected_date_to) {
    return "";
  }
  if (component.selected_date_from > component.selected_date_to) {
    return "Date from cannot be later than date to.";
  }
  return "";
};

/**
 * Validation, payload-building, and submission methods for the report builder.
 */
export default {
  validate_question_metrics() {
    if (!this.is_question_statistics_summary) {
      return true;
    }
    if (!this.selected_questions.length) {
      this.show_error("Please select at least one question.");
      return false;
    }
    const has_missing_metrics = this.selected_questions.some(
      (question) => !(question.selected_metric_type_ids || []).length
    );
    if (has_missing_metrics) {
      this.show_error("Please select at least one statistic for each question.");
      return false;
    }
    return true;
  },
  get_template_validation_message() {
    if (this.save_as_template && !this.template_name) {
      return "Template name is required.";
    }
    return "";
  },
  get_basics_validation_message() {
    if (!this.selected_report_type_id) {
      return "Please select a report type.";
    }
    if (!this.selected_output_format_id) {
      return "Please select an output format.";
    }
    return "";
  },
  get_columns_validation_message() {
    if (!this.has_available_report_fields) {
      return this.missing_report_fields_message;
    }
    if (!this.selected_field_ids.length) {
      return "Please select at least one column.";
    }
    return "";
  },
  get_scope_validation_message() {
    if (
      this.template_scope_internal_name === "selected_entity_types" &&
      !this.template_entity_type_ids.length
    ) {
      return "Please select at least one entity type.";
    }
    return this.get_date_validation_message();
  },
  get_date_validation_message() {
    const validation_messages = [
      get_missing_date_dimension_message(this),
      get_missing_date_boundary_message(this),
      get_invalid_date_order_message(this),
    ];
    const message = validation_messages.find(Boolean) || "";
    return message;
  },
  validate_builder_state() {
    const validation_methods = [
      this.get_template_validation_message,
      this.get_basics_validation_message,
      this.get_columns_validation_message,
      this.get_scope_validation_message,
    ];
    const has_error = validation_methods.some((validation_method) =>
      show_validation_error(this, validation_method.call(this))
    );
    if (has_error) {
      return false;
    }
    return this.validate_question_metrics();
  },
  build_column_rows() {
    const rows = build_report_columns({
      selected_field_ids: this.selected_field_ids,
      required_field_id: this.required_field_id,
      field_display_labels: this.field_display_labels,
    });
    return rows;
  },
  build_question_scope_filter_rows() {
    const rows = build_question_scope_filters({
      selected_questions: this.selected_questions,
      filter_type_id: this.get_lookup_id("filter_types", "section_question_any_of"),
    });
    return rows;
  },
  build_answer_filter_rows() {
    const rows = build_question_answer_filters({
      selected_questions: this.selected_questions,
      filter_type_id: this.get_lookup_id("filter_types", "section_question_answer_any_of"),
    });
    return rows;
  },
  build_filters() {
    if (!this.is_question_scope_report) {
      return [];
    }
    const filters = [
      ...this.build_question_scope_filter_rows(),
      ...this.build_answer_filter_rows(),
    ];
    return filters;
  },
  build_question_metrics() {
    if (!this.is_question_statistics_summary) {
      return [];
    }
    const rows = build_question_metric_rows({
      selected_questions: this.selected_questions,
    });
    return rows;
  },
  resolve_template_name() {
    if (this.save_as_template) {
      return this.template_name;
    }
    return build_one_off_template_name(
      this.initial_entity_name,
      this.selected_report_type_label
    );
  },
  build_template_payload() {
    const payload = {
      project_id: Number(this.project_id),
      report_type_id: this.selected_report_type_id,
      scope_type_id: this.get_lookup_id("scope_types", this.template_scope_internal_name),
      name: this.resolve_template_name(),
      description: this.save_as_template ? this.template_description || null : null,
      is_active: this.save_as_template,
      entity_type_ids: this.template_entity_type_ids,
      date_dimension_id: this.selected_date_dimension_id || null,
      date_from: this.selected_date_from || null,
      date_to: this.selected_date_to || null,
      columns: this.build_column_rows(),
      filters: this.build_filters(),
      question_metrics: this.build_question_metrics(),
    };
    return payload;
  },
  create_template_request() {
    if (this.is_editing_template) {
      return axios.post(
        UPDATE_REPORT_TEMPLATE_URL,
        {
          ...this.build_template_payload(),
          template_id: this.editing_template_id,
          is_active: true,
        },
        { headers: this.headers }
      );
    }
    return axios.post(CREATE_REPORT_TEMPLATE_URL, this.build_template_payload(), {
      headers: this.headers,
    });
  },
  create_report_request(template_id) {
    const payload = {
      template_id,
      output_format_id: this.selected_output_format_id,
    };
    if (this.target_entity_profile_id) {
      payload.target_entity_profile_id = this.target_entity_profile_id;
    }
    return axios.post(GENERATE_MANAGED_REPORT_URL, payload, {
      headers: this.headers,
    });
  },
  handle_report_generation_success(report_response) {
    this.generated_report = report_response.data.report;
    this.show_success("Report generated successfully.");
  },
  handle_report_generation_error(error) {
    if (error.response?.data?.report) {
      this.generated_report = error.response.data.report;
    }
    this.show_error(extract_report_builder_error_message(error));
  },
  finalize_report_generation(loader) {
    this.generating_report = false;
    return this.close_loader(loader);
  },
  submit_report_generation() {
    if (!this.validate_builder_state()) {
      return Promise.resolve(null);
    }
    this.generating_report = true;
    this.generated_report = null;
    return this.show_loader("Generating report...")
      .then((loader) => this.create_template_request()
        .then((template_response) =>
          this.create_report_request(template_response.data.template.id)
        )
        .then((report_response) => {
          this.handle_report_generation_success(report_response);
        })
        .catch((error) => {
          this.handle_report_generation_error(error);
        })
        .finally(() => this.finalize_report_generation(loader)));
  },
};
